import { BaseModel } from './BaseModel';
import type {
  OriginWorkOrder,
  StateWorkOrder,
  WorkOrderType,
} from './workOrder';

export enum QualityAnswerType {
  OkKo = 0,
  OkKoNa = 1,
  Numeric = 2,
  Text = 3,
  Options = 4,
}
export enum QualityPhotoRequirement {
  No = 0,
  Optional = 1,
  Required = 2,
}
export enum QualityChecklistStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  NotSent = 3,
}
export enum QualityRejectAction {
  Reopen = 0,
  NewLinkedCorrective = 1,
  Ask = 2,
}
export enum QualityAttachSource {
  Rule = 0,
  Manual = 1,
}
export enum QualityAnswerResult {
  Unanswered = 0,
  Ok = 1,
  Ko = 2,
  Na = 3,
}

type PinnedOrdinal<T extends true> = T;

export type QualityEnumOrdinalAssertions = [
  PinnedOrdinal<QualityAnswerType.OkKo extends 0 ? true : false>,
  PinnedOrdinal<QualityAnswerType.OkKoNa extends 1 ? true : false>,
  PinnedOrdinal<QualityAnswerType.Numeric extends 2 ? true : false>,
  PinnedOrdinal<QualityAnswerType.Text extends 3 ? true : false>,
  PinnedOrdinal<QualityAnswerType.Options extends 4 ? true : false>,
  PinnedOrdinal<QualityPhotoRequirement.No extends 0 ? true : false>,
  PinnedOrdinal<QualityPhotoRequirement.Optional extends 1 ? true : false>,
  PinnedOrdinal<QualityPhotoRequirement.Required extends 2 ? true : false>,
  PinnedOrdinal<QualityChecklistStatus.Pending extends 0 ? true : false>,
  PinnedOrdinal<QualityChecklistStatus.Approved extends 1 ? true : false>,
  PinnedOrdinal<QualityChecklistStatus.Rejected extends 2 ? true : false>,
  PinnedOrdinal<QualityChecklistStatus.NotSent extends 3 ? true : false>,
  PinnedOrdinal<QualityRejectAction.Reopen extends 0 ? true : false>,
  PinnedOrdinal<
    QualityRejectAction.NewLinkedCorrective extends 1 ? true : false
  >,
  PinnedOrdinal<QualityRejectAction.Ask extends 2 ? true : false>,
  PinnedOrdinal<QualityAttachSource.Rule extends 0 ? true : false>,
  PinnedOrdinal<QualityAttachSource.Manual extends 1 ? true : false>,
  PinnedOrdinal<QualityAnswerResult.Unanswered extends 0 ? true : false>,
  PinnedOrdinal<QualityAnswerResult.Ok extends 1 ? true : false>,
  PinnedOrdinal<QualityAnswerResult.Ko extends 2 ? true : false>,
  PinnedOrdinal<QualityAnswerResult.Na extends 3 ? true : false>,
];

export interface QualityAnswerOption {
  id: string;
  label: string;
  result: QualityAnswerResult;
}

export interface QualityAnswerOptionRequest {
  id?: string;
  label: string;
  result: QualityAnswerResult;
}

export interface QualityChecklistTemplateItem {
  id: string;
  text: string;
  order: number;
  answerType: QualityAnswerType;
  required: boolean;
  koBlocks: boolean;
  commentRequiredOnKo: boolean;
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  photo: QualityPhotoRequirement;
  options?: QualityAnswerOption[] | null;
  allowMultiple: boolean;
}

export interface QualityChecklistTemplate extends BaseModel {
  id: string;
  name: string;
  description?: string | null;
  order: number;
  onRejectAction?: QualityRejectAction | null;
  items: QualityChecklistTemplateItem[];
  activeRuleCount: number;
}

export interface QualityChecklistTemplateItemRequest {
  id?: string;
  text: string;
  answerType: QualityAnswerType;
  required: boolean;
  koBlocks: boolean;
  commentRequiredOnKo: boolean;
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  photo: QualityPhotoRequirement;
  options?: QualityAnswerOptionRequest[] | null;
  allowMultiple: boolean;
}

export interface CreateQualityChecklistTemplateRequest {
  name: string;
  description?: string | null;
  onRejectAction?: QualityRejectAction | null;
  items: QualityChecklistTemplateItemRequest[];
}

export interface UpdateQualityChecklistTemplateRequest extends CreateQualityChecklistTemplateRequest {
  active: boolean;
}

export interface QualityChecklistRule extends BaseModel {
  id: string;
  templateId: string;
  templateName: string;
  order: number;
  workOrderTypes: WorkOrderType[];
  origins: OriginWorkOrder[];
  assetIds: string[];
  assetSubtreeRootIds: string[];
  showAlways?: boolean;
}

export interface CreateQualityChecklistRuleRequest {
  templateId: string;
  workOrderTypes: WorkOrderType[];
  origins: OriginWorkOrder[];
  assetIds: string[];
  assetSubtreeRootIds: string[];
  showAlways: boolean;
}

export interface UpdateQualityChecklistRuleRequest extends CreateQualityChecklistRuleRequest {
  active: boolean;
}

export interface ApplyQualityChecklistRuleRequest {
  states: StateWorkOrder[];
  createdFrom?: string;
  createdTo?: string;
}

export interface QualityChecklistApplyPreview {
  ruleId: string;
  templateId: string;
  templateName: string;
  candidates: number;
  toApply: number;
  skippedAlreadyHas: number;
  skippedNotWinner: number;
  limit: number;
}

export interface QualityChecklistApplyResult {
  ruleId: string;
  templateId: string;
  templateName: string;
  candidates: number;
  applied: number;
  skippedAlreadyHas: number;
  skippedNotWinner: number;
  skippedNoLongerEligible: number;
  limit: number;
}

export interface QualityValidationConfig {
  defaultRejectAction: QualityRejectAction;
  autoSendOnComplete: boolean;
}

export interface UpdateQualityAutoSendRequest {
  autoSendOnComplete: boolean;
}

export interface UpdateQualityValidationConfigRequest {
  defaultRejectAction: QualityRejectAction;
}

export interface WorkOrderQualityAnswer {
  result: QualityAnswerResult;
  numericValue?: number | null;
  textValue?: string | null;
  optionIds?: string[] | null;
  comment?: string | null;
  answeredAt: string;
  answeredByUserId: string;
  answeredByName: string;
}

export interface WorkOrderQualityPhoto {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface WorkOrderQualityChecklistItem {
  itemId: string;
  text: string;
  order: number;
  answerType: QualityAnswerType;
  required: boolean;
  koBlocks: boolean;
  commentRequiredOnKo: boolean;
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  photo: QualityPhotoRequirement;
  options?: QualityAnswerOption[] | null;
  allowMultiple: boolean;
  answer?: WorkOrderQualityAnswer | null;
  photos: WorkOrderQualityPhoto[];
}

export interface WorkOrderQualityChecklist {
  id: string;
  templateId: string;
  templateName: string;
  status: QualityChecklistStatus;
  rejectPolicy: QualityRejectAction;
  items: WorkOrderQualityChecklistItem[];
  attachedAt: string;
  attachSource: QualityAttachSource;
  ruleId?: string | null;
  attachedByUserId?: string | null;
  sentAt?: string | null;
  sentByName?: string | null;
  reviewerUserId?: string | null;
  reviewerName?: string | null;
  reviewedAt?: string | null;
  rejectComment?: string | null;
  rejectAction?: QualityRejectAction | null;
  derivedWorkOrderId?: string | null;
  derivedWorkOrderCode?: string | null;
}

export interface AttachQualityChecklistRequest {
  templateId: string;
}

export interface QualityChecklistItemAnswerRequest {
  itemId: string;
  result?: QualityAnswerResult | null;
  numericValue?: number | null;
  textValue?: string | null;
  optionIds?: string[] | null;
  comment?: string | null;
}

export interface SaveQualityChecklistAnswersRequest {
  answers: QualityChecklistItemAnswerRequest[];
}

export interface RejectQualityChecklistRequest {
  comment: string;
  action?: QualityRejectAction | null;
}

export interface QualityPhotoReadUrl {
  url: string;
  expiresAt: string;
}

export type QualityValidationErrorCode =
  | 'WORKORDER_QUALITY_PENDING'
  | 'QUALITY_CHECKLIST_ALREADY_PENDING'
  | 'QUALITY_CHECKLIST_NOT_PENDING'
  | 'QUALITY_CHECKLIST_INCOMPLETE'
  | 'QUALITY_CHECKLIST_BLOCKED'
  | 'QUALITY_CHECKLIST_NOT_CORRECTIVE'
  | 'QUALITY_CHECKLIST_WORKORDER_FINAL'
  | 'QUALITY_TEMPLATE_INVALID'
  | 'QUALITY_TEMPLATE_INACTIVE'
  | 'QUALITY_RULE_INVALID'
  | 'QUALITY_ANSWER_INVALID'
  | 'QUALITY_REJECT_COMMENT_REQUIRED'
  | 'QUALITY_REJECT_COMMENT_TOO_LONG'
  | 'QUALITY_REJECT_ACTION_REQUIRED'
  | 'QUALITY_REJECT_ACTION_INVALID'
  | 'QUALITY_PHOTO_INVALID'
  | 'QUALITY_PHOTO_LIMIT'
  | 'QUALITY_PERMISSION_DENIED'
  | 'QUALITY_IMPERSONATION_REFUSED'
  | 'QUALITY_WORKORDER_INVOICED'
  | 'QUALITY_APPLY_INVALID'
  | 'QUALITY_APPLY_RULE_NOT_APPLICABLE'
  | 'QUALITY_APPLY_LIMIT_EXCEEDED';

export interface QualityValidationErrorBody {
  code: QualityValidationErrorCode;
  workOrderIds?: string[];
  workOrderId?: string;
  checklistId?: string;
  currentStatus?: QualityChecklistStatus;
  field?: string;
  reason?: string;
  itemIds?: string[];
  required?: string;
  total?: number;
  limit?: number;
  totalIsLowerBound?: boolean;
}

export interface QualityValidationFailure {
  ok: false;
  status: number;
  error: QualityValidationErrorBody | null;
}

export interface QualityValidationSuccess<T> {
  ok: true;
  data: T;
}

export interface QualityQueueCounts {
  pending: number;
  validated: number;
}

export type QualityValidationResult<T> =
  QualityValidationSuccess<T> | QualityValidationFailure;

export default QualityChecklistTemplate;
